import { useForm } from "react-hook-form";
import { useState } from "react";
import axios from "axios";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useGetDispatchCenters } from "@/api/dispatch-centers";
import {
  type CreateTripFormSchema,
  createTripSchema,
  useCreateTrip,
} from "@/api/trips";

export function CreateTripForm({ onSuccess }: { onSuccess?: () => void } = {}) {
  const [submitError, setSubmitError] = useState<string | null>(null);
  const form = useForm<CreateTripFormSchema>({
    resolver: zodResolver(createTripSchema),
    defaultValues: {
      dispatchCenterId: "",
      color: "#3B82F6", // Default blue color
    },
  });

  // Fetch dispatch centers for the dispatch-center selection
  const { data: dispatchCenters, isLoading } = useGetDispatchCenters();

  const createTripMutation = useCreateTrip({
    onSuccess: () => {
      setSubmitError(null);
      form.reset();
      onSuccess?.();
    },
    onError: (error) => {
      const response = axios.isAxiosError(error) ? error.response?.data : null;
      setSubmitError(
        response && typeof response.error === "string"
          ? response.error
          : "Failed to create trip. Try again."
      );
    },
  });

  const onSubmit = form.handleSubmit((values) => {
    setSubmitError(null);
    createTripMutation.mutate(values);
  });

  return (
    <Form {...form}>
      <form onSubmit={onSubmit} className="space-y-8">
        {submitError && (
          <p role="alert" className="text-sm text-red-700">
            {submitError}
          </p>
        )}
        {Object.keys(form.formState.errors).length > 0 && (
          <div className="rounded-md bg-red-50 p-4">
            <div className="text-sm text-red-700">
              Please fix the following errors:
              <ul className="mt-2 list-disc pl-5">
                {Object.entries(form.formState.errors).map(([key, error]) => (
                  <li key={key}>
                    {error?.message?.toString() ?? `Invalid ${key}`}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        )}

        <FormField
          control={form.control}
          name="dispatchCenterId"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Dispatch Center</FormLabel>
              <Select onValueChange={field.onChange} defaultValue={field.value}>
                <FormControl>
                  <SelectTrigger>
                    <SelectValue placeholder="Select a dispatch center" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  {isLoading ? (
                    <SelectItem value="loading">
                      Loading dispatch centers...
                    </SelectItem>
                  ) : dispatchCenters && dispatchCenters.length > 0 ? (
                    dispatchCenters.map((center) => (
                      <SelectItem key={center.id} value={center.id}>
                        {center.name}
                      </SelectItem>
                    ))
                  ) : (
                    <SelectItem value="no-centers">
                      No dispatch centers available
                    </SelectItem>
                  )}
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="color"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Trip Color</FormLabel>
              <div className="flex gap-2">
                <FormControl>
                  <Input
                    type="color"
                    {...field}
                    className="h-10 w-20 cursor-pointer"
                  />
                </FormControl>
                <Input
                  type="text"
                  value={field.value ?? ""}
                  onChange={field.onChange}
                  placeholder="#RRGGBB"
                  className="flex-1"
                />
              </div>
              <FormMessage />
            </FormItem>
          )}
        />

        <Button
          type="submit"
          disabled={createTripMutation.isPending}
          className="w-full"
        >
          {createTripMutation.isPending ? "Creating..." : "Create Trip"}
        </Button>
      </form>
    </Form>
  );
}
